import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { UnstyledButton } from '@mantine/core';
import {
  IconUser,
  IconSettings,
  IconLock,
  IconLogout,
  IconCircleFilled,
  IconMoodSmile,
} from '@tabler/icons-react';
import { Text, Loader } from '@/components/ui';
import { Group, Stack, Box } from '@/components/ui/layout';
import { Menu } from '@/components/ui/overlays';
import { SegmentedControl } from '@/components/ui/navigation';
import { Avatar } from '@/components/ui/data-display';
import { EmojiStatusPicker } from '@/components/ui/EmojiStatusPicker';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { usePreferencesStore } from '@/stores';
import { usePresenceManagerStore } from '@/stores/presenceManagerStore';

/**
 * PresenceStatus type
 * Matches SDK PresenceStatus enum values
 * Using local type to avoid SDK export conflicts
 */
type PresenceStatus = 'Online' | 'Away' | 'Busy' | 'Offline';

interface UserProfileMenuProps {
  onLockScreen?: () => void;
}

export default function UserProfileMenu({ onLockScreen }: UserProfileMenuProps) {
  const navigate = useNavigate();
  const { user, logout } = useEnrichedAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [emojiPickerOpened, setEmojiPickerOpened] = useState(false);

  // Get presence status from preferences store (persisted for offline fallback)
  const presenceStatus = usePreferencesStore((state) => state.presenceStatus);
  const setPresenceStatus = usePreferencesStore((state) => state.setPresenceStatus);

  // Use selective PresenceManager subscriptions - only subscribe to what we need
  const isConnected = usePresenceManagerStore((state) => state.isConnected);
  const currentUserPresence = usePresenceManagerStore((state) => state.currentUserPresence);
  const updateStatus = usePresenceManagerStore((state) => state.updateStatus);

  // Current emoji and status message from activityMessage
  const activityMessage = currentUserPresence?.activityMessage || '';
  const emojiMatch = activityMessage.match(/^([^\s]+)\s/);
  const currentEmoji = emojiMatch?.[1] || '';
  const currentMessage = emojiMatch ? activityMessage.replace(/^[^\s]+\s/, '') : activityMessage;

  // Sync PresenceManager with local preferences
  const currentStatus = (currentUserPresence?.status || presenceStatus) as PresenceStatus;

  const handlePresenceChange = async (value: string) => {
    try {
      // Update local preferences (for persistence and offline fallback)
      setPresenceStatus(value as any);

      // Update PresenceManager (real-time sync)
      if (isConnected) {
        await updateStatus(value as any);
      } else {
        console.warn('[UserProfileMenu] WebSocket not connected - status saved locally only');
      }
    } catch (error: any) {
      console.error('[UserProfileMenu] Failed to update presence:', error?.message || error);
    }
  };

  const handleLogout = async () => {
    // Show custom logout modal
    setIsLoggingOut(true);

    try {
      // Call logout and wait for it to complete
      // This will clear tokens, user data, and notify the backend
      // PresenceProvider will automatically disconnect from PresenceHub
      await logout();

      // Redirect to login page
      navigate('/auth/login', { replace: true });

      // Keep the logout modal visible until the next page has rendered
      // Use requestAnimationFrame to wait for browser paint cycles
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Wait an additional moment to ensure login page is fully visible
          setTimeout(() => {
            setIsLoggingOut(false);
          }, 800);
        });
      });
    } catch (error) {
      console.error('[UserProfileMenu] Logout error:', error);
      // Continue to redirect even if logout fails
      navigate('/auth/login', { replace: true });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setIsLoggingOut(false);
          }, 800);
        });
      });
    }
  };

  const handleLockScreen = () => {
    if (onLockScreen) {
      onLockScreen();
    }
  };

  const handleEmojiStatusSave = async (emoji: string, message: string) => {
    try {
      // Combine emoji and message
      const fullMessage = emoji ? `${emoji} ${message}` : message;

      // Update activity message via PresenceManager (keeps current status)
      if (isConnected) {
        const currentStatusValue = currentUserPresence?.status || 'Online';
        await updateStatus(currentStatusValue as any, fullMessage);
      } else {
        console.warn('[UserProfileMenu] Cannot update emoji status - WebSocket not connected');
      }
    } catch (error) {
      console.error('[UserProfileMenu] Failed to update emoji status:', error);
    }
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const name = user.displayName || user.username || 'User';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getPresenceColor = (status: PresenceStatus) => {
    switch (status) {
      case 'Online':
        return 'green';
      case 'Away':
        return 'yellow';
      case 'Busy':
        return 'red';
      case 'Offline':
      default:
        return 'gray';
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Menu position="bottom-end" shadow="md" width={240}>
        <Menu.Target>
          <UnstyledButton>
            <Group gap="xs">
              <Box style={{ position: 'relative' }}>
                <Avatar
                  radius="xl"
                  size="sm"
                  color="indigo"
                  src={user.avatar || undefined}
                >
                  {getUserInitials()}
                </Avatar>
                {/* Presence Indicator */}
                <Box
                  style={{
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: `var(--mantine-color-${getPresenceColor(currentStatus)}-6)`,
                    border: '2px solid var(--mantine-color-body)',
                  }}
                />
              </Box>
              <Box visibleFrom="sm">
                <Text size="sm" fw={600} lh={1.2}>
                  {user.displayName || user.username}
                </Text>
                <Text size="xs" c="dimmed" lh={1.2}>
                  {user.email}
                </Text>
              </Box>
            </Group>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          {/* User Info Header */}
          <Box p="xs" pb="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" fw={600}>
              {user.displayName || user.username}
            </Text>
            <Text size="xs" c="dimmed">
              {user.email}
            </Text>
          </Box>

          <Menu.Label>Account</Menu.Label>

          <Menu.Item
            leftSection={<IconUser size={14} />}
            onClick={() => navigate('/profile')}
          >
            Profile
          </Menu.Item>

          <Menu.Item
            leftSection={<IconSettings size={14} />}
            onClick={() => navigate('/settings')}
          >
            Settings
          </Menu.Item>

          <Menu.Divider />

          <Menu.Label>Presence Status</Menu.Label>

          {/* Emoji Status Display/Edit */}
          {activityMessage && (
            <Box px="xs" py="xs" mb="xs">
              <Group gap="xs" style={{ padding: '0.5rem', backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: '4px' }}>
                {currentEmoji && <Text size="lg">{currentEmoji}</Text>}
                <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                  {currentMessage || activityMessage}
                </Text>
              </Group>
            </Box>
          )}

          <Menu.Item
            leftSection={<IconMoodSmile size={14} />}
            onClick={() => setEmojiPickerOpened(true)}
          >
            {activityMessage ? 'Edit Status Message' : 'Set Status Message'}
          </Menu.Item>

          <Menu.Divider />

          <Box px="xs" py="sm">
            <SegmentedControl
              value={currentStatus}
              onChange={handlePresenceChange}
              fullWidth
              size="xs"
              color="gray"
              data={[
                {
                  value: 'Online',
                  label: (
                    <Group gap={4} justify="center" wrap="nowrap">
                      <IconCircleFilled size={8} style={{ color: 'var(--mantine-color-green-6)' }} />
                      <Text size="xs" style={{ whiteSpace: 'nowrap' }}>Online</Text>
                    </Group>
                  ),
                },
                {
                  value: 'Away',
                  label: (
                    <Group gap={4} justify="center" wrap="nowrap">
                      <IconCircleFilled size={8} style={{ color: 'var(--mantine-color-yellow-6)' }} />
                      <Text size="xs" style={{ whiteSpace: 'nowrap' }}>Away</Text>
                    </Group>
                  ),
                },
                {
                  value: 'Busy',
                  label: (
                    <Group gap={4} justify="center" wrap="nowrap">
                      <IconCircleFilled size={8} style={{ color: 'var(--mantine-color-red-6)' }} />
                      <Text size="xs" style={{ whiteSpace: 'nowrap' }}>Busy</Text>
                    </Group>
                  ),
                },
              ]}
            />
          </Box>

          <Menu.Divider />

          <Menu.Item
            leftSection={<IconLock size={14} />}
            onClick={handleLockScreen}
          >
            Lock Screen
          </Menu.Item>

          <Menu.Divider />

          <Menu.Item
            color="red"
            leftSection={<IconLogout size={14} />}
            onClick={(e) => {
              console.log('[UserProfileMenu] Logout menu item clicked!', e);
              handleLogout();
            }}
          >
            Logout
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      {/* Logout Modal - Rendered via Portal to persist during navigation */}
      {isLoggingOut && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.2s ease-in',
          }}
        >
          <Box
            style={{
              backgroundColor: 'var(--mantine-color-body)',
              padding: '3rem',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            }}
          >
            <Stack align="center" gap="lg">
              {/* Loader */}
              <Loader size="lg" />

              {/* Logout Message */}
              <Stack gap={4} align="center">
                <Text size="xl" fw={700}>
                  Logging out...
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  Please wait while we securely log you out
                </Text>
              </Stack>
            </Stack>
          </Box>
        </div>,
        document.body
      )}

      {/* Emoji Status Picker Modal */}
      <EmojiStatusPicker
        opened={emojiPickerOpened}
        onClose={() => setEmojiPickerOpened(false)}
        onSave={handleEmojiStatusSave}
        currentEmoji={currentEmoji}
        currentMessage={currentMessage}
      />
    </>
  );
}
